/**
 * Rich email template editor (TipTap): bold/italic/underline, headings, lists,
 * font family & size, text & highlight color, links, images (file + paste as base64 in HTML).
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { FontFamily } from '@tiptap/extension-font-family';
import Placeholder from '@tiptap/extension-placeholder';
import { Extension } from '@tiptap/core';
import Link from '@tiptap/extension-link';

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize?.replace(/['"]+/g, '') || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).run(),
    };
  },
});

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];
const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times', value: '"Times New Roman", Times, serif' },
  { label: 'Courier', value: 'Courier New, monospace' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Trebuchet', value: '"Trebuchet MS", sans-serif' },
];

const STYLE_ID = 'compose-template-richtext-styles';

function ensureGlobalStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    .compose-template-rich-editor .ProseMirror {
      min-height: 220px;
      max-height: min(50vh, 420px);
      overflow-y: auto;
      padding: 12px;
      outline: none;
      line-height: 1.5;
    }
    .compose-template-rich-editor .ProseMirror p { margin: 0 0 0.5em 0; }
    .compose-template-rich-editor .ProseMirror img {
      max-width: 100%;
      height: auto;
      vertical-align: middle;
      border-radius: 4px;
    }
    .compose-template-rich-editor .ProseMirror p.is-editor-empty:first-child::before {
      color: var(--text-muted, #8b949e);
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  `;
  document.head.appendChild(el);
}

function ToolbarBtn({ onClick, active, title, children, disabled }) {
  return (
    <button
      type="button"
      className={`ct-rich-tb-btn${active ? ' ct-rich-tb-btn-active' : ''}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function MergeFieldTiptapSelect({ editor, fields }) {
  const list = Array.isArray(fields) ? fields : [];
  return (
    <select
      className="comms-merge-field-select ct-rich-merge-select"
      value=""
      aria-label="Insert merge field"
      disabled={!editor}
      onChange={(e) => {
        const t = e.target.value;
        if (t && editor) {
          editor.chain().focus().insertContent(`(${t})`).run();
          e.target.value = '';
        }
      }}
    >
      <option value="">Insert field…</option>
      {list.map((f) => (
        <option key={f.token} value={f.token}>
          {f.label} — ({f.token})
        </option>
      ))}
    </select>
  );
}

export default function ComposeTemplateRichEditor({
  content = '',
  onChange,
  placeholder = 'Write your message… Paste images directly here.',
  mergeFields = [],
  className = '',
  /** When set with toggle handler, shows a “Preview” control next to Insert field (Compose page). */
  composeRecipientPreview = null,
  onComposeRecipientPreviewToggle = null,
  /** Compose only: hide the editor surface while preview is open so the toolbar stays next to the preview (avoids scrolling the toggle away). */
  collapseEditorSurface = false,
}) {
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    ensureGlobalStyles();
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        link: false,
      }),
      TextStyle,
      FontSize,
      FontFamily.configure({ types: ['textStyle'] }),
      Color.configure({ types: ['textStyle'] }),
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { style: 'max-width:100%;height:auto;' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '<p></p>',
    editorProps: {
      attributes: {
        class: 'compose-template-rich-prose',
      },
      handlePaste(_view, event) {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (let i = 0; i < items.length; i += 1) {
          const item = items[i];
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) return true;
            const reader = new FileReader();
            reader.onload = () => {
              const src = reader.result;
              const ed = editorRef.current;
              if (ed && typeof ed.chain === 'function') {
                ed.chain().focus().setImage({ src }).run();
              }
            };
            reader.readAsDataURL(file);
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!editor || content === undefined) return;
    const current = editor.getHTML();
    const next = content || '<p></p>';
    if (next !== current && next.replace(/\s/g, '') !== current.replace(/\s/g, '')) {
      editor.commands.setContent(next, false);
    }
  }, [content, editor]);

  const pickImageFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;
      if (!file.type.startsWith('image/')) {
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        editor.chain().focus().setImage({ src: reader.result }).run();
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    },
    [editor]
  );

  if (!editor) {
    return <div className="compose-template-rich-editor compose-template-rich-loading">Loading editor…</div>;
  }

  const showComposePreviewToggle = typeof onComposeRecipientPreviewToggle === 'function';
  const previewActive = Boolean(composeRecipientPreview && composeRecipientPreview.active);

  return (
    <div className={`compose-template-rich-editor ${className}`.trim()}>
      <input ref={fileInputRef} type="file" accept="image/*" className="ct-rich-file-input" onChange={onFileChange} />
      <div className="ct-rich-toolbar">
        <ToolbarBtn
          title="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <span className="ct-rich-ul">U</span>
        </ToolbarBtn>
        <ToolbarBtn
          title="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <s>S</s>
        </ToolbarBtn>
        <span className="ct-rich-tb-sep" />
        <ToolbarBtn title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H2
        </ToolbarBtn>
        <ToolbarBtn title="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          H3
        </ToolbarBtn>
        <ToolbarBtn title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          •
        </ToolbarBtn>
        <ToolbarBtn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1.
        </ToolbarBtn>
        <span className="ct-rich-tb-sep" />
        <label className="ct-rich-color-wrap" title="Text color">
          <span className="ct-rich-color-label">A</span>
          <input
            type="color"
            className="ct-rich-color-input"
            onInput={(ev) => editor.chain().focus().setColor(ev.target.value).run()}
          />
        </label>
        <label className="ct-rich-color-wrap" title="Highlight">
          <span className="ct-rich-hl-label">Hl</span>
          <input
            type="color"
            className="ct-rich-color-input"
            onInput={(ev) => editor.chain().focus().setHighlight({ color: ev.target.value }).run()}
          />
        </label>
        <button type="button" className="ct-rich-tb-btn" title="Remove highlight" onClick={() => editor.chain().focus().unsetHighlight().run()}>
          No Hl
        </button>
        <span className="ct-rich-tb-sep" />
        <select
          className="ct-rich-select"
          title="Font"
          onChange={(ev) => {
            const v = ev.target.value;
            if (!v) editor.chain().focus().unsetFontFamily().run();
            else editor.chain().focus().setFontFamily(v).run();
            ev.target.value = '';
          }}
          value=""
        >
          <option value="">Font…</option>
          {FONT_FAMILIES.filter((f) => f.value).map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          className="ct-rich-select"
          title="Size"
          onChange={(ev) => {
            const v = ev.target.value;
            if (!v) editor.chain().focus().unsetFontSize().run();
            else editor.chain().focus().setFontSize(v).run();
            ev.target.value = '';
          }}
          value=""
        >
          <option value="">Size…</option>
          {FONT_SIZES.map((sz) => (
            <option key={sz} value={sz}>
              {sz}
            </option>
          ))}
        </select>
        <span className="ct-rich-tb-sep" />
        <ToolbarBtn
          title="Link"
          active={editor.isActive('link')}
          onClick={() => {
            const prev = editor.getAttributes('link').href;
            const url = window.prompt('Link URL', prev || 'https://');
            if (url === null) return;
            if (url === '') {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              return;
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }}
        >
          Link
        </ToolbarBtn>
        <ToolbarBtn title="Insert image from file" onClick={pickImageFile}>
          <i className="fas fa-image" />
        </ToolbarBtn>
        <MergeFieldTiptapSelect editor={editor} fields={mergeFields} />
        {showComposePreviewToggle && (
          <button
            type="button"
            className={`ct-rich-tb-btn${previewActive ? ' ct-rich-tb-btn-active' : ''}`}
            onClick={onComposeRecipientPreviewToggle}
            title={previewActive ? 'Return to editing' : 'Preview how this email looks to the recipient'}
          >
            <i className="fas fa-eye" aria-hidden /> Preview
          </button>
        )}
      </div>
      <div
        className={`ct-rich-editor-frame${collapseEditorSurface ? ' ct-rich-editor-frame--collapsed' : ''}`.trim()}
        aria-hidden={collapseEditorSurface}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
